import * as React from 'react';
import { atom, selectorFamily, useSetRecoilState } from 'recoil';

import type { TableCellTarget } from '@gitbook/api-client';
import { conflicts } from '@gitbook/doc-core';

interface HoveredCommentNodeState {
    node: string;
    /** Show button to post button */
    postButton: boolean;
    /** Show highlighting background on the node */
    highlighting: boolean;
}

/**
 * State storing the document node to highlight for commenting.
 */
export const hoveredCommentNodeAtom = atom<null | HoveredCommentNodeState>({
    key: 'hoveredCommentNodeAtom',
    default: null,
});

/**
 * Recoil selector to test if a node is the currently hovered node for comments.
 */
export const isCommentNodeHoveredSelector = selectorFamily<boolean, string>({
    key: 'isCommentNodeHoveredSelector',
    get:
        (node) =>
        ({ get }) => {
            const hoveredNode = get(hoveredCommentNodeAtom);
            return !!(hoveredNode?.highlighting && hoveredNode?.node === node);
        },
});

/**
 * React hook to update the current highlighted node.
 */
export function useSetHoveredCommentNode(): {
    highlight: (node: string | null, params?: Omit<HoveredCommentNodeState, 'node'>) => void;
} {
    const setHoveredCommentNode = useSetRecoilState(hoveredCommentNodeAtom);

    return React.useMemo(() => {
        return {
            highlight: (
                nodeInput,
                params = {
                    highlighting: true,
                    postButton: true,
                }
            ) => {
                const node = nodeInput ? conflicts.getOriginalKey(nodeInput) : null;

                setHoveredCommentNode((prev) => {
                    if (!node) {
                        return null;
                    }

                    if (prev && prev.node === node && prev.postButton) {
                        // if we were showing the postButton for the same node,
                        // we will still show it, regardless of the params
                        // in order to avoid layout shifts.
                        params.postButton = prev.postButton;
                    }
                    return { node, ...params };
                });
            },
        };
    }, [setHoveredCommentNode]);
}

// Serialize the record and definition so we don't need to use a complex object to create a unique key for a table cell
export function getSerializedTableCellKey(tableCell?: TableCellTarget) {
    return tableCell ? `${tableCell.record}:${tableCell.definition}` : undefined;
}
